import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import {
  DialogActions,
  Typography,
  Button,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/styles';
import { DownloadRounded } from '@mui/icons-material';
import { delimitNumber } from '@src/utils/number';
import { useTranslation } from 'react-i18next';
import { removeVietnameseTones } from '@src/utils/string';
import { QR_CODE_URL, TEMPLATE_QR_CODE } from '@src/configs';
import WarningIcon from '@src/assets/icons/warning-orange.svg';
import CopyableButton from '@src/components/CopyableButton';
import { checkVietNam } from '@src/utils/checkCountry';
import { COLOR } from '@src/styles/color';
import apis from '@src/apis';
import { ORDER_STATUS } from '@src/constants/order';
import actions from '@src/redux/actions';
import { PACKAGE_TYPE } from '@src/constants/package';
import { BANK_TEMPLATE_QR_CODE, DEFAULT_BANK } from '@src/constants/payment';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  StyledPaymentDialogV2,
  StyledTransferInformation,
} from '../index.style';

const BANK_TRANSFERRED_TIMEOUT = 15;

const BankTransferDialogV2 = ({
  open,
  selectedBank,
  totalPrice,
  onChangeLoading,
  onRedirectToPaymentHistory,
  onCloseBankTransferDialog,
  isDubbingRoute,
}) => {
  const theme = useTheme();
  const [usdToVndRate, setUsdToVndRate] = useState(1);
  const [time, setTime] = useState(BANK_TRANSFERRED_TIMEOUT);

  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { latestOrder, latestDubbingOrder } = useSelector(
    (state) => state.user,
  );

  const [nextTime] = useState(moment());

  const { getFeatureValue } = useFeatureFlags();

  const usePaymentQRV2 = getFeatureValue(FEATURE_KEYS.PAYMENT_QR_V2, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const isVietNam = checkVietNam();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleCancelPendingOrder = async (note) => {
    if (!isDubbingRoute && latestOrder.status !== ORDER_STATUS.PENDING) return;
    if (isDubbingRoute && latestDubbingOrder.status !== ORDER_STATUS.PENDING)
      return;

    onChangeLoading(true);
    const data = await apis.orders.cancelOrder(
      isDubbingRoute ? latestDubbingOrder?.id : latestOrder.id,
      note,
    );
    onChangeLoading(false);
    if (!data.status) {
      dispatch(actions.noti.push({ severity: 'error', code: data.code }));
    } else if (data.result.type === PACKAGE_TYPE.STUDIO)
      dispatch(actions.user.getLatestOrderSuccess(data.result));
    else if (data.result.type === PACKAGE_TYPE.DUBBING)
      dispatch(actions.user.getLatestDubbingOrderSuccess(data.result));
  };

  const handleCancelPendingOrderByUser = async () => {
    handleCancelPendingOrder();
    onCloseBankTransferDialog();
  };

  const handleConfirmTransferOrder = async () => {
    if (!isDubbingRoute && latestOrder.status !== ORDER_STATUS.PENDING) return;
    if (isDubbingRoute && latestDubbingOrder.status !== ORDER_STATUS.PENDING)
      return;
    onChangeLoading(true);
    const data = await apis.orders.confirmByCustomer(
      isDubbingRoute ? latestDubbingOrder?.id : latestOrder.id,
    );
    onChangeLoading(false);
    if (data?.status) {
      if (data.result.type === PACKAGE_TYPE.STUDIO)
        dispatch(actions.user.getLatestOrderSuccess(data.result));
      if (data.result.type === PACKAGE_TYPE.DUBBING)
        dispatch(actions.user.getLatestDubbingOrderSuccess(data.result));
      onRedirectToPaymentHistory();
      return;
    }
    dispatch(actions.noti.push({ severity: 'error', message: data?.message }));
  };

  const fetchUsdToVndRate = async () => {
    const data = await apis.banks.getUSDToVNDRate();
    if (data.status) {
      const { result } = data;
      setUsdToVndRate(result || 1);
    }
  };

  useEffect(() => {
    fetchUsdToVndRate();
  }, [isVietNam]);

  const currentOrder = useMemo(
    () => (isDubbingRoute ? latestDubbingOrder : latestOrder),
    [isDubbingRoute, latestDubbingOrder, latestOrder],
  );

  const getQRTemplate = (bankName) => {
    switch (bankName) {
      case 'Vietcombank':
        return BANK_TEMPLATE_QR_CODE.VIETCOMBANK;
      case 'Techcombank':
        return BANK_TEMPLATE_QR_CODE.TECHCOMBANK;
      case DEFAULT_BANK.MBBANK:
        return BANK_TEMPLATE_QR_CODE.MBBANK;
      default:
        return TEMPLATE_QR_CODE;
    }
  };

  const qrCodeURL = useMemo(() => {
    const qrTemplage = getQRTemplate(currentOrder.bank?.name);
    return `${QR_CODE_URL}/image/${selectedBank?.name}-${
      selectedBank?.number
    }-${qrTemplage}.png?amount=${
      isVietNam ? totalPrice : Math.ceil(totalPrice * usdToVndRate)
    }&addInfo=${currentOrder.code}&accountName=${selectedBank?.holder}`;
  }, [selectedBank, currentOrder, isVietNam, totalPrice, usdToVndRate]);

  const handleDownloadQR = async () => {
    const response = await fetch(qrCodeURL);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'qr-code.png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (nextTime) {
      const interval = setInterval(() => {
        const diffTime = moment().diff(moment(nextTime), 'seconds');
        if (diffTime <= BANK_TRANSFERRED_TIMEOUT) {
          const remainingSecond = BANK_TRANSFERRED_TIMEOUT - diffTime;
          setTime(remainingSecond);
        } else {
          setTime(0);
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
    return null;
  }, [nextTime]);

  return (
    <StyledPaymentDialogV2 open={open}>
      <div className="wrapper">
        <Typography className="title">{t('transferPayments')}</Typography>
        <StyledTransferInformation usePaymentQRV2={usePaymentQRV2}>
          <div className="qr-wrapper">
            <img src={qrCodeURL} alt="bank-qr" className="bank-qr" />
            <div className="button-group">
              <Button
                size="small"
                className="saveQR"
                variant="outlined"
                onClick={handleDownloadQR}
                data-id="button-download-qr"
              >
                <DownloadRounded />
                {t('downloadQR')}
              </Button>
            </div>
          </div>

          <div className="content-wrapper">
            <div className="content-information">
              <div className="row-content">
                <Typography className="label">{t('bank')}:</Typography>
                <Typography className="value">
                  {removeVietnameseTones(selectedBank?.address, language)}
                </Typography>
              </div>
              <div className="row-content">
                <Typography className="label">{t('accountOwner')}:</Typography>
                <Typography className="value">
                  {removeVietnameseTones(selectedBank?.holder, language)}
                </Typography>
              </div>
              <div className="row-content">
                <Typography className="label">{t('cardNumber')}:</Typography>
                <Typography className="value card-number">
                  {selectedBank?.bankNumber || selectedBank?.number}
                  {isMobile && (
                    <CopyableButton
                      dataAttribute={{ 'data-id': 'copy-card-number' }}
                      color={COLOR.secondary[64]}
                      content={selectedBank?.bankNumber || selectedBank?.number}
                      fontSize={24}
                    />
                  )}
                </Typography>
              </div>
              <div className="row-content">
                <Typography className="label">
                  {t('amountToTransfer')}:
                </Typography>
                <Typography className="value">
                  {!isVietNam && <span className="unit-price">$</span>}
                  {isVietNam && (
                    <span className="unit-price underlined">đ</span>
                  )}
                  {delimitNumber(totalPrice, language)}
                </Typography>
              </div>
              {isMobile && (
                <div className="row-content">
                  <Typography className="label">
                    {t('transferContent')}:
                  </Typography>
                  <Typography className="value card-number">
                    {isDubbingRoute
                      ? latestDubbingOrder.code
                      : latestOrder.code}
                    <CopyableButton
                      dataAttribute={{ 'data-id': 'copy-transfer-content' }}
                      content={
                        isDubbingRoute
                          ? latestDubbingOrder.code
                          : latestOrder.code
                      }
                      color={COLOR.secondary[64]}
                      fontSize={24}
                    />
                  </Typography>
                </div>
              )}
              <div style={{ marginBottom: '16px' }} />
            </div>
          </div>
          <div className="note">
            <div className="note-item">
              <img className="icon" src={WarningIcon} alt="warning" />
              <div className="note-content">
                <Typography className="note-description">
                  {isMobile
                    ? t('bankTransferNoteMobileWeb')
                    : t('bankTransferNoteDesktopWeb')}
                </Typography>
              </div>
            </div>
          </div>
        </StyledTransferInformation>
        <DialogActions
          usePaymentQRV2={usePaymentQRV2}
          classes={{ root: 'dialog-action' }}
        >
          <Button
            variant="contained"
            color="primary"
            className="confirm-order-button"
            fullWidth
            disabled={time > 0}
            onClick={handleConfirmTransferOrder}
            sx={{
              '&.Mui-disabled': {
                background: COLOR.primary,
                color: COLOR.indigo[100],
                opacity: 0.5,
              },
            }}
            data-id="confirm-transfer"
            data-order-id={
              isDubbingRoute ? latestDubbingOrder.id : latestOrder.id
            }
          >
            {`${t('transferredConfirmation')} ${time > 0 ? `(${time}s)` : ''}`}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCancelPendingOrderByUser}
            className="cancel-order-button"
            fullWidth
            data-id="cancel-transfer"
            data-order-id={
              isDubbingRoute ? latestDubbingOrder.id : latestOrder.id
            }
          >
            {t('cancelPayment')}
          </Button>
        </DialogActions>
      </div>
    </StyledPaymentDialogV2>
  );
};

export default BankTransferDialogV2;
